import { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Text } from '@/components/ui/text';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  CurrentUserDocument,
  DeleteCurrentUserDocument,
  UpdateCurrentUserDocument,
} from '~/graphql/generated/graphql';
import { FullHeightWrapper } from '~/layout/components/full-height-wrapper/FullHeightWrapper';
import { Icon } from '~/global/components/icon/Icon';
import { useDeleteProtection } from '~/global/hooks/useDeleteProtection';
import { useConfirmation } from '~/global/hooks/useConfirmation';
import { useAuth } from '~/auth/contexts/auth/Auth';
import { usePurchases } from '~/purchases/contexts/purchases/Purchases';
import { useRouter } from 'expo-router';

/** The backend rejects deletion with this code while a subscription still renews. */
const ACTIVE_SUBSCRIPTION_ERROR = 'USER__ACTIVE_SUBSCRIPTION';

export interface UserSettingsScreenProps {}

interface UserSettingsFormData {
  firstName: string;
  lastName: string;
}

export const UserSettingsScreen = ({}: UserSettingsScreenProps) => {
  const { t } = useTranslation('user');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { handleLogout } = useAuth();
  const router = useRouter();
  const { hasRenewingSubscription, openManageSubscriptions, refresh } =
    usePurchases();

  const { data: userData } = useQuery(CurrentUserDocument);
  const currentUser = userData?.currentUser;

  const [updateUser, { loading: updating }] = useMutation(
    UpdateCurrentUserDocument,
  );

  const [deleteUser, { loading: deleting }] = useMutation(
    DeleteCurrentUserDocument,
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<UserSettingsFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
    },
  });

  useEffect(() => {
    if (currentUser) {
      reset({
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
      });
    }
  }, [currentUser, reset]);

  const onSubmit = async (data: UserSettingsFormData) => {
    try {
      await updateUser({
        variables: {
          input: {
            firstName: data.firstName,
            lastName: data.lastName,
          },
        },
      });
      reset(data);
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  // Shown when the user still has an auto-renewing subscription: they must cancel it
  // at the store before the account can be deleted.
  const { dispatchConfirmationDialog: dispatchManageSubscription } =
    useConfirmation(openManageSubscriptions, {
      title: t('settings.dangerZone.subscriptionActiveTitle'),
      description: t('settings.dangerZone.subscriptionActiveDescription'),
      confirmText: t('settings.dangerZone.manageSubscription'),
    });

  const handleDeleteAccount = async () => {
    try {
      await deleteUser();
      await handleLogout();
      router.replace('/');
    } catch (error) {
      // Backend backstop: if a subscription is still renewing (stale client state),
      // re-sync and route the user to cancel it instead of failing silently.
      if (
        error instanceof Error &&
        error.message.includes(ACTIVE_SUBSCRIPTION_ERROR)
      ) {
        await refresh();
        dispatchManageSubscription();
        return;
      }
      console.error('Failed to delete account:', error);
    }
  };

  const { dispatchConfirmationDialog: showDeleteConfirmationDialog } =
    useDeleteProtection(handleDeleteAccount, {
      description: t('settings.dangerZone.deleteWarning'),
    });

  // Route the delete action: cancel-subscription dialog when one is still renewing,
  // otherwise the normal delete confirmation.
  const requestDelete = () =>
    hasRenewingSubscription
      ? dispatchManageSubscription()
      : showDeleteConfirmationDialog();

  return (
    <FullHeightWrapper>
      <ScrollView
        className="flex-1"
        contentContainerClassName="p-4 md:pt-20"
        showsVerticalScrollIndicator={false}
      >
        <View className="gap-y-6 pb-6 w-full">
          {/* Profile Section */}
          <View className="gap-y-4">
            <Text className="text-lg font-semibold">
              {t('settings.profile.title')}
            </Text>

            {/* Email (read-only) */}
            <View className="gap-y-2">
              <Text className="text-sm font-medium">
                {t('settings.profile.email')}
              </Text>
              <Input
                value={currentUser?.email || ''}
                editable={false}
                className="bg-muted"
              />
            </View>

            {/* First Name and Last Name */}
            <View className="flex flex-col md:flex-row gap-4">
              {/* First Name */}
              <View className="gap-y-2 flex-1">
                <Text className="text-sm font-medium">
                  {t('settings.profile.firstName')}
                </Text>
                <Controller
                  control={control}
                  name="firstName"
                  rules={{
                    required: t('settings.validation.firstNameRequired'),
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <View>
                      <Input
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        placeholder={t('settings.profile.firstName')}
                      />
                      {errors.firstName && (
                        <Text className="text-destructive text-xs mt-1">
                          {errors.firstName.message}
                        </Text>
                      )}
                    </View>
                  )}
                />
              </View>

              {/* Last Name */}
              <View className="gap-y-2 flex-1">
                <Text className="text-sm font-medium">
                  {t('settings.profile.lastName')}
                </Text>
                <Controller
                  control={control}
                  name="lastName"
                  rules={{
                    required: t('settings.validation.lastNameRequired'),
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <View>
                      <Input
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        placeholder={t('settings.profile.lastName')}
                      />
                      {errors.lastName && (
                        <Text className="text-destructive text-xs mt-1">
                          {errors.lastName.message}
                        </Text>
                      )}
                    </View>
                  )}
                />
              </View>
            </View>

            {/* Save Button */}
            <Button
              onPress={handleSubmit(onSubmit)}
              disabled={!isDirty}
              loading={updating}
              className="mt-2 md:self-start"
            >
              <Text>{t('settings.profile.save')}</Text>
            </Button>
          </View>

          {/* Danger Zone Section */}
          <View className="gap-y-4 border-t border-border pt-6">
            <Text className="text-lg font-semibold text-destructive">
              {t('settings.dangerZone.title')}
            </Text>

            {!showDeleteConfirmation ? (
              <Button
                onPress={() =>
                  hasRenewingSubscription
                    ? dispatchManageSubscription()
                    : setShowDeleteConfirmation(true)
                }
                variant="outline"
                className="flex flex-row gap-x-2 items-center border-destructive md:self-start"
                textClassName="text-destructive"
              >
                <Icon name="Trash2" size={14} className="text-destructive" />
                <Text>{t('settings.dangerZone.deleteAccount')}</Text>
              </Button>
            ) : (
              <View className="gap-y-3 p-4 border border-destructive rounded-md bg-destructive/10">
                <Text className="text-sm font-medium text-destructive">
                  {t('settings.dangerZone.confirmDelete')}
                </Text>
                <Text className="text-sm text-muted-foreground">
                  {t('settings.dangerZone.deleteWarning')}
                </Text>
                <View className="flex flex-row gap-x-2 mt-2">
                  <Button
                    onPress={requestDelete}
                    variant="destructive"
                    loading={deleting}
                    className="flex-1 md:flex-none"
                  >
                    <Text>{t('settings.dangerZone.confirmButton')}</Text>
                  </Button>
                  <Button
                    onPress={() => setShowDeleteConfirmation(false)}
                    variant="outline"
                    className="flex-1 md:flex-none"
                  >
                    <Text>{t('settings.dangerZone.cancelButton')}</Text>
                  </Button>
                </View>
              </View>
            )}
          </View>
        </View>
      </ScrollView>
    </FullHeightWrapper>
  );
};
